import type { ReactNode } from 'react'
import type { MeasurementWithUnit } from 'screens/TripDashboard/TripDashboard.types'

export enum WorkflowType {
  DEFAULT = 'DEFAULT',
  SECURE_CLOUD_DEPLOYMENT = 'SECURE_CLOUD_DEPLOYMENT',
}

export interface LegData {
  tripCid?: string
  shipmentNumber: string
  workflowType?: WorkflowType
  attributes: ShipmentAttributes
  collectionInstructions?: string
  deliveryInstructions?: string
  wayPoints: StopData[]
  paperlessAvailable: boolean
  pharmaExtendedInformation?: PharmaExtendedInformation
  troAvailable?: boolean
}

export interface PharmaExtendedInformation {
  productType?: string
  minimum?: number
  maximum?: number
  setPoint?: number
  minUnit?: string
  maxUnit?: string
  setPointUnit?: string
  shipmentNumbers?: (string | undefined)[]
}

export interface BulkLegData {
  tripCid: string
  legs: LegData[]
}

export interface ShipmentAttributes {
  weight: number
  weightWithUnit?: MeasurementWithUnit
  volume: number
  volumeWithUnit?: MeasurementWithUnit
  loadingMeters: number
  loadingMetersWithUnit?: MeasurementWithUnit
  numberOfItems: number
  numberOfPallets?: number
  cargoItemDetails: CargoItemDetails[]
  dangerousGoodsDetails: DangerousGoodsDetails[]
  customerReferences: string[]
  pupReferences: string[]
  delReferences: string[]
  uitReference?: UitReference
  shipmentReferences: CodeReference[]
}

export interface CodeReference {
  code: string
  value: string
}

export interface StopData {
  wayPointCid: string
  sequence: number
  type: 'PUP' | 'CUS' | 'DEL'
  addressType: string
  address: StopAddress
  geoPoint?: GeoPoint
  geofenceRadius?: number
  polygonRegion?: GeoPoint[]
  earlyDateTime: string
  lateDateTime: string
  statuses: StopStatus[]
  availableStatuses: AvailableStopStatus[]
  availableRegularStatuses: AvailableStopStatus[]
  availableDiscrepancyStatuses: AvailableStopStatus[]
  documents?: StopDocument[]
  isProofAttached?: boolean
  palletsDocumentRequired?: boolean
  palletsDocumentAttached?: boolean
  shipmentAddresses?: ShipmentAddress[]
  partyIdentifier: string
  customerIdentifier: string
  references?: CodeReference[]
}

export interface StopAddress {
  name: string[]
  street: string[]
  city: string[]
  zipCode?: string
  countryCode: string
}

export interface ShipmentAddress {
  type: string
  name: string[]
  street: string[]
  city: string[]
  zipCode?: string
  country: string
  typeOfBusiness: string
}

export interface Geofence {
  id: string
  type: string
  name: string
  geometry: {
    coordinates: {
      lon: number
      lat: number
    }[]
    radius?: number,
    type: string
  }
}

export interface GeoPoint {
  latitude: number
  longitude: number
  timestamp?: string
  lastTimestamp?: string
  distanceToPrevious?: number
  timeToPrevious?: number // seconds
  heading?: number
  estimatedSpeed?: number
}

export interface GeoPointsGroup {
  label?: string
  geoPoints: GeoPoint[]
}

export interface StopStatus {
  id: string
  statusCode: string
  reasonCode?: string
  comment?: string
  createdAt: string
  createdAtOffset?: number
  principalId?: string
}

type StatusTypeRule = 'MANDATORY' | 'OPTIONAL'
type StatusFieldRule = 'DISABLED' | 'NOT_REQUIRED' | 'REQUIRED'

export interface AvailableStopStatus {
  id: string
  code: string
  reasonCode?: string
  statusType: StatusTypeRule
  proofs: StatusFieldRule
  attachments: StatusFieldRule
  comments: StatusFieldRule
  signee: StatusFieldRule
  discrepancy?: boolean
  bulkStatusUpdateOnly?: boolean
}

export interface StopDocument {
  id: string
  name: string
  createdAt: string
  checksum: string
  fileType: string
}

export interface AttachmentData {
  fileId: string
  fileType: string
}

export interface CargoItemDetails {
  description: string
  numberOfPackages: number
  packagesType: string
  weight: number
  weightUnit: string
  volume: number
  volumeUnit: string
  marksAndNumbers: string
  size: CargoItemSize
  sizeUnit: string
  loadingMeters: number
  loadingMetersUnit: string
  stackable: boolean
  innerPackage: number
  innerPackageType: string
}

export interface CargoItemSize {
  length: number
  width: number
  height: number
}

export interface DangerousGoodsDetails {
  unNumber?: string
  numberOfPackages: number
  packagesType?: string
  description: string
  classType: string
  packingGroup: string
  quantity: number
  quantityType?: string
}

export interface MetricValue {
  tripCid: string
  tripNumber: string
  deviceId: string
  updateSource: string
  updateTime: string
  receivedTime: string
}

export interface VehiclePositionMetricValue extends MetricValue {
  latitude: string
  longitude: string
}

export interface VehicleTemperatureMetricValue extends MetricValue {
  sensorId: string
  temperature: number
}

export interface StopsSequenceGroup {
  id: number
  stopLegPairs: StopLegPair[]
  state: 'pending' | 'in_progress' | 'completed'
  pickupsCount: number
  deliveriesCount: number
  customsCount: number
}

export interface StopLegPair {
  stop: StopData
  leg: LegData
}

export interface UitReference {
  code?: string
  isRequired?: boolean
}

export interface TripLogData {
  entityId: string
  createdAt: string
  type: string
  data?: GeofenceTripLogData | StatusTripLogData
}

export interface GeofenceTripLogData {
  name: string
  geoPoint?: GeoPoint
  wayPointCid?: string
  offset?: string
}

export interface StatusTripLogData {
  createdAt: string
  shipmentNumber: string
  statusCode: string
  reasonCode?: string
  wayPointCid: string
  offset?: string
  geoPoint?: GeoPoint
}

export interface ShortestRoute {
  routeId: string
  coordinates: GeoPoint[]
}

export interface ParkingLocation {
  entityId: string
  routeId: string
  name: string
  type: ParkingLocationType
  geometry: {
    coordinates: GeoPoint[]
    type: string
  }
  properties: {
    color: string
    title: string
    address: string
    charges: string
    currency: string
    group_name: string
    opening_hours: string
    security_code: string
    long_description?: string
    short_description: string
    number_of_parking_lots: string
    running_reefers_allowed: string
  }
}

export enum ParkingLocationType {
  KUEHNE_NAGEL = 'KUEHNE_NAGEL',
  STRATEGIC = 'STRATEGIC',
  BASIC_SECURITY = 'BASIC_SECURITY',
  MEDIUM_SECURITY = 'MEDIUM_SECURITY',
  HIGH_SECURITY = 'HIGH_SECURITY',
  NEGATIVE_PARKING = 'NEGATIVE_PARKING',
  SHORT_BREAK_45MIN = 'SHORT_BREAK_45MIN',
  OTHER = 'OTHER',
}

export enum PrincipalId {
  SYSTEM = 'SYSTEM',
}

export interface KNDataCardAction {
  label?: string
  handler: () => void
  variant?: 'contained' | 'outlined' | 'text'
  color?: 'inherit' | 'error' | 'warning' | 'primary' | 'secondary' | 'info' | 'success'
  icon?: ReactNode
  disabled?: boolean
}

export interface ShareParkingLocationViaEmailFormValues {
  email: string
}

export interface ShareParkingLocationViaSmsFormValues {
  phoneNumber: string
}

export interface ProReferenceFormValues {
  proReference: string
}
